/**
 * Deterministic 32-bit PRNG (mulberry32). One instance per stream, injected (FS-02 §3.1, R33).
 * The exact integer operations are normative — determinism depends on them.
 */
import { hashSeed } from './hash';

export interface Rng {
  readonly name: string;
  getState(): number;
  setState(state: number): void;

  next(): number; // uniform [0, 1)
  nextUint32(): number;
  int(min: number, maxExclusive: number): number;
  range(min: number, max: number): number;
  bool(p?: number): boolean;
  pick<T>(items: readonly T[]): T;
  shuffle<T>(items: T[]): void;

  /** Independent child stream, deterministic in (this.getState(), name). Does not advance this. */
  fork(name: string): Rng;
  clone(): Rng;
}

class Mulberry32 implements Rng {
  constructor(
    public readonly name: string,
    private state: number,
  ) {
    this.state = state >>> 0;
  }

  getState(): number {
    return this.state >>> 0;
  }
  setState(state: number): void {
    this.state = state >>> 0;
  }

  nextUint32(): number {
    // Reference step from FS-02 §3.1.
    let a = (this.state + 0x6d2b79f5) | 0;
    this.state = a;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return (t ^ (t >>> 14)) >>> 0;
  }

  next(): number {
    return this.nextUint32() / 4294967296;
  }

  int(min: number, maxExclusive: number): number {
    const span = maxExclusive - min;
    if (span <= 0) return min;
    return min + Math.floor(this.next() * span);
  }

  range(min: number, max: number): number {
    return min + this.next() * (max - min);
  }

  bool(p = 0.5): boolean {
    return this.next() < p;
  }

  pick<T>(items: readonly T[]): T {
    if (items.length === 0) throw new Error(`Rng.pick on empty array (${this.name})`);
    return items[this.int(0, items.length)]!;
  }

  shuffle<T>(items: T[]): void {
    for (let i = items.length - 1; i > 0; i--) {
      const j = this.int(0, i + 1);
      const tmp = items[i]!;
      items[i] = items[j]!;
      items[j] = tmp;
    }
  }

  fork(name: string): Rng {
    const childSeed = (hashSeed(name) ^ Math.imul(this.getState(), 0x9e3779b1)) >>> 0;
    return new Mulberry32(`${this.name}:${name}`, childSeed);
  }

  clone(): Rng {
    return new Mulberry32(this.name, this.state);
  }
}

export function createRng(seed: number, name: string): Rng {
  return new Mulberry32(name, seed >>> 0);
}

export { hashSeed };
